import { useState, useMemo, useEffect } from "react";
import {
  Col,
  Container,
  Row,
  Modal,
  Alert,
  Button as BsButton,
  CloseButton,
} from "react-bootstrap";
import { useForm } from "react-hook-form";
import {
  useNavigate,
  useLoaderData,
  useRouteLoaderData,
  useLocation,
  Link,
} from "react-router-dom";
import { Input } from "./Controls/Input";
import { MediumPage } from "./Controls/MediumPage";
import { Select } from "./Controls/Select";
import { Api } from "./Data/Api";
import {
  IntegrationStatus,
  SubscriptionStatus,
  SubscriptionUpdateReqApi,
} from "./Data/ApiTransport";
import { gridOneCol, parseBoolean, useParamIds } from "./Data/Common";
import { cents, showMoney } from "./Data/Money";
import { processResult } from "./Data/Result";
import {
  FormattedAuditEntry,
  ParkerDetails,
  ParkerInfo,
  Subscription,
  SubscriptionParker,
  Vehicle,
} from "./Data/ApiTypes";
import {
  parseSubscriptionParker,
  parseSubscriptionUpdateReq,
} from "./Data/ApiParse";
import { Button } from "./Controls/Button";
import {
  filterStatusOptions,
  getActionsInstructions,
  showSubscriptionStatus,
} from "./Data/Subscription";
import { localDateToInstantUtc, showDate } from "./Data/Date";
import {
  CarFront,
  CreditCard,
  CurrencyDollar,
  ExclamationCircleFill,
  FileText,
  Hash,
  JournalText,
  PeopleFill,
  Trash,
} from "react-bootstrap-icons";
import { getInstructions, StatusOption } from "./Data/Subscription";
import { PageBar } from "./Controls/PageBar";
import { emitSubscription } from "./Data/ApiEmit";
import { isPositive } from "dinero.js";
import { toast } from "react-toastify";
import { DataGrid } from "./Controls/DataGrid";
import { ColDef } from "ag-grid-community";
import { Hyperlink } from "./Controls/Hyperlink";
import { ConfirmModal } from "./Controls/ConfirmModal";
import {
  InviteParkerForm,
  InviteParkerModal,
} from "./UserPlanDetailsPageInviteParkerModal";
import { LocalDate } from "@js-joda/core";
import ReactivatePlanModal from "./UserPlanReactivationModal";
import UserPlanCancellationModal from "./UserPlanCancellationModal";
import { useAtom } from "jotai";
import { authAtom } from "./Data/Atoms";
import ParkerEditModal from "./ParkerEditModal";
import { createApiService } from "./shared/apiConfig";
import { AuditApi, ContractsRuleApi, DataEntitiesFacilityIntegrationParty, FacilityApi, ServerRequestsParkerIntegrationPartyDto, ServerRequestsSubscriptionGroupsResponse, SubscriptionApi, SubscriptionGroupsApi, SubscriptionStaffApi, WaitlistApi } from "./shared/api/axios-client";
import QrCodeModal from "./Controls/QRCodeModal";
import { ParkerIntegrations } from "./ParkerIntegrations";
import { isAdminRole } from "./helpers/RoleManager";
import { SubscriptionAuditLogModal } from "./SubscriptionAuditLogModal";
import { buildAuditChanges, formatAllEvents } from "./helpers/AuditLogsHelper";
import { ChooseVehicleModal } from "./Controls/ChooseVehicleModal";
import { AddVehicleModal } from "./Controls/AddVehicleModal";
import { VehiclesSection } from "./VehiclesSection";
import { GroupModal } from "./GroupModal";
import { useSubscriptionVehiclesAndIntegrations } from "./useSubscriptionVehiclesAndIntegrations";
import { ContractRuleForm, ContractRuleModal, RuleType } from "./ContractRuleModal";
import { CostProjectionDto, CostProjectionSection } from "./CostProjectionSection";
import { mapCostProjection } from "./CostProjectionMapper";

// Allowed per-vehicle third-party integrations
export const SUBSCRIPTION_ALLOWED_THIRD_PARTY = ["FlashApi", "CurbTracFlatFile", "Tiba"];
// Allowed per-vehicle third-party integrations
export const VEHICLE_ALLOWED_THIRD_PARTY = ["Ballparc", "CurbTrac"];

export function FacilityParkerSubscriptionEditPage() {
  const nav = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  
  const [msg, setMsg] = useState<string>();
  const [showQuantityModal, setShowQuantityModal] = useState(false);
  const [showQuantityDecreaseModal, setShowQuantityDecreaseModal] =
    useState(false);
  const [
    showQuantityDecreaseParkersModal,
    setShowQuantityDecreaseParkersModal,
  ] = useState(false);
  const [modalSelection, setModalSelection] = useState<boolean | undefined>(
    undefined
  );
  const [pendingSubmit, setPendingSubmit] =
    useState<SubscriptionUpdateReqApi | null>(null);
  const { facilityId, subscriptionId } = useParamIds();
  const [loadedSub, vehicles, initialParkerIntegrations, loadedGroups] = useLoaderData() as [
    Subscription,
    Vehicle[],
    ServerRequestsParkerIntegrationPartyDto[],
    ServerRequestsSubscriptionGroupsResponse[]
  ];
  const [sub, setSub] = useState(loadedSub);
  const [parkers, setParkers] = useState(sub.parkers);
  const parker = useRouteLoaderData("parker") as ParkerDetails;
  const [showReactivationModal, setShowReactivationModal] = useState(false);
  const [showCancellationModal, setShowCancellationModal] = useState(false);
  const [showParkerEditModal, setShowParkerEditModal] = useState(false);
  const [selectedParker, setSelectedParker] = useState<SubscriptionParker>();
  const [selectedParkerSub, setSelectedParkerSub] = useState<any>();
  const [isStatusDirty, setIsStatusDirty] = useState(false);
  const [auth, setAuth] = useAtom( authAtom );
  const [showQrModal, setShowQrModal] = useState(false);
  const [qrCodeData, setQrCodeData] = useState('');
  const [showToggleConfirm, setShowToggleConfirm] = useState(false);
  const [pendingToggle, setPendingToggle] = useState<{
    integrationPartyId: number;
    subscriptionId: number;
    parkerId: number;
    vehicleId?: number;
    facilityId: number;
    newStatus: IntegrationStatus;
  } | null>(null);
  const [showAudit, setShowAudit] = useState(false);
  const [auditEntries, setAuditEntries] = useState<FormattedAuditEntry[]>([]);
  const [showAddVehicleModal, setShowAddVehicleModal] = useState(false);
  const [vehicleToDelete, setVehicleToDelete] = useState<Vehicle | null>(null);
  const [showParkerVehicleRemoveConfirm, setShowParkerVehicleRemoveConfirm] = useState(false);
  const [showFinalizeConfirm, setShowFinalizeConfirm] = useState(false);
  const [pendingFinalizeData, setPendingFinalizeData] = useState<SubscriptionUpdateReqApi | null>(null);
  const [chooseModalVehicles, setChooseModalVehicles] = useState<Vehicle[]>([]);
  const [chooseModalExclude, setChooseModalExclude] = useState<number[]>([]);
  const [showChoose, setShowChoose] = useState(false);
  const [parkerIntegrations, setParkerIntegrations] = useState(initialParkerIntegrations);
  const [vehicleIntegrations, setVehicleIntegrations] = useState<
      DataEntitiesFacilityIntegrationParty[]
  >([]);
  const [subscriptionIntegrations, setSubscriptionIntegrations] = useState<
      DataEntitiesFacilityIntegrationParty[]
  >([]);
  const [showAddContract, setShowAddContract] = useState(false);
  const [editingRules, setEditingRules] = useState<ContractRuleForm[]>([]);
  const [editingGroupId, setEditingGroupId] = useState<number | undefined>(undefined);
  const [currentProjection, setCurrentProjection] = useState<CostProjectionDto | null>(null);
  const [nextProjection, setNextProjection] = useState<CostProjectionDto | null>(null);

  const {
    vehicleAdd,
    vehicleLink,
    vehicleRemove,
    openChooseModalForParkersList,
    handleToggleIntegration,
  } = useSubscriptionVehiclesAndIntegrations({
    facilityId,
    parkerId: parker.parkerId,
    subscriptionId,
    vehicles: sub.vehicles,
    setSub,
    setParkerIntegrations,
    setChooseModalVehicles,
    setChooseModalExclude,
    setShowChoose,
  });

  const waitlistApi = createApiService(WaitlistApi);
  const subscriptionStaffApi = createApiService(SubscriptionStaffApi);
    const contractsApi = createApiService(ContractsRuleApi);

  //remove parker
  const [showRemove, setShowRemove] = useState(false);
  const [removeParker, setRemoveParker] = useState<ParkerInfo>();

  const [showInvite, setShowInviteParker] = useState(false);

  //groups
  const [groups, setGroups] =  useState<ServerRequestsSubscriptionGroupsResponse[]>(loadedGroups);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState<ServerRequestsSubscriptionGroupsResponse | undefined>();
  const [showRemoveGroup, setShowRemoveGroup] = useState(false);
  const [groupToRemove, setGroupToRemove] =
    useState<ServerRequestsSubscriptionGroupsResponse | undefined>();
    
  const subGroupsApi = createApiService(SubscriptionGroupsApi);

  const quantity = sub.quantity;
  const isEnhancedGroup = sub.isEnhancedGroup === true;
  const canManageGroups = sub.status === SubscriptionStatus.Active || sub.status === SubscriptionStatus.NeedsSetup;

  const initialValues = emitSubscription(sub);
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    getValues,
    reset,
    formState: { isDirty, dirtyFields, errors },
  } = useForm<SubscriptionUpdateReqApi>({
    defaultValues: initialValues,
  });

  const values = watch();

  const userIsAdmin = isAdminRole(auth.account?.roles ?? []);

  useEffect(() => {
    if (!userIsAdmin) return;

    async function fetchIntegrations() {
      if (!facilityId) return;

      try {
        const api = createApiService(FacilityApi);
        const response = await api.getFacilityIntegrations({ facilityId });

        const vehiclesIntegrations = response.data.filter(
          (x) =>
            VEHICLE_ALLOWED_THIRD_PARTY.includes(x.integrationParty?.name || "") &&
            x.isActive
        );

        const mergedVehiclesIntegrations: ServerRequestsParkerIntegrationPartyDto[] = [
          ...parkerIntegrations.filter((fi) =>
            VEHICLE_ALLOWED_THIRD_PARTY.includes(fi.integrationName || "")
          ),

          ...sub.vehicles.flatMap((vehicle) =>
            vehiclesIntegrations
              .filter(
                (fi) =>
                  !parkerIntegrations.some(
                    (pi) =>
                      pi.integrationPartyId === fi.integrationPartyId &&
                      pi.vehicleId === vehicle.vehicleId
                  )
              )
              .map((fi) => ({
                integrationPartyId: fi.integrationPartyId,
                integrationName: fi.integrationParty?.name || "Ballparc",
                parkerId: parker.parkerId,
                vehicleId: vehicle.vehicleId,
                isActive: false,
                metaFlashUID: "",
                metaFlashRFIDNumber: "",
              }))
          ),
        ].filter((x): x is NonNullable<typeof x> => x != null);

        setVehicleIntegrations(mergedVehiclesIntegrations);

        const subscriptionsIntegrations = response.data.filter(
          (x) =>
            SUBSCRIPTION_ALLOWED_THIRD_PARTY.includes(x.integrationParty?.name || "") &&
            !parkerIntegrations.some(
              (i) => i.integrationPartyId === x.integrationPartyId
            ) &&
            x.isActive
        );

        const mergedSubscriptionsIntegrations: ServerRequestsParkerIntegrationPartyDto[] = [
        ...parkerIntegrations.filter((fi) =>
          SUBSCRIPTION_ALLOWED_THIRD_PARTY.includes(fi.integrationName || "")
        ),
        ...subscriptionsIntegrations.map((fi) => ({
          integrationPartyId: fi.integrationPartyId,
          integrationName: fi.integrationParty?.name,
          parkerId: parker.parkerId,
          isActive: false,
          metaFlashUID: "",
          metaFlashRFIDNumber: "",
        })),
      ].filter((x): x is NonNullable<typeof x> => x != null);

        setSubscriptionIntegrations(mergedSubscriptionsIntegrations);
      } catch (err: any) {
        toast.error("An unexpected error occurred");
      }
    }

    fetchIntegrations();
  }, [facilityId, userIsAdmin, parkerIntegrations, sub.vehicles]);

  const normalizedAudit = useMemo(() => {
  const built = buildAuditChanges(auditEntries || []);

    return built.filter((e) => e.eventName !== "SubscriptionDecisionEvent") 
    .filter((e) => e.changes && e.changes.length > 0);
  }, [auditEntries]);

  async function openAuditLog() {
    const api = createApiService(AuditApi);
    const result = await api.getAuditEntriesAsync({
      entityType: "Subscription",
      entityId: subscriptionId,
      operatorId: sub.facility.operatorId,
    });
    const normalized = formatAllEvents(result.data.auditEntities!);
    setAuditEntries(normalized);
    setShowAudit(true);
  }

  const cancel = {
    lbl: "Plan Is Cancelled",
    expl: "",
    val: SubscriptionStatus.Active, // set the plan as active, if it's cancelled
  };

  const reactivate = {
    lbl: "Plan Is Active",
    expl: "",
    val: SubscriptionStatus.Cancelled, // set the plan as cancelled, if it's active
  };

  const numberOfSpotsAllocated = sub.parkers.reduce(
    (sum, p) => sum + (p.quantity || 0),
    0
  );
  
  const onSubmitInternal = async (data: SubscriptionUpdateReqApi, isFinalizing: boolean = false) => {
    // If finalizing setup, set status to FinalizeSetup to indicate to backend
    if (isFinalizing) {
      data.status = SubscriptionStatus.FinalizeSetup;
    }
    
    if (data.status === SubscriptionStatus.Waitlisted) {
    try {
      var response = await waitlistApi.addToWaitlist({
        serverRequestsAddToWaitlistRequest: {
          parkerId: parker.parkerId,
          facilityId: facilityId,
          planEntryId: sub.planEntryId,
          spacesRequested: data.quantity,
          isAddingToExistingParker: false
        },
      });

      if (response.data.error)
      {
        toast.error(response.data.error);
        return;
      }

      toast.success("Added to waitlist successfully."); 
    } catch {
      toast.error("An unexpected error occurred. Please try again later.");
      return;
    }
    }

    data.isAccountsReceivable = sub.isAccountsReceivable;
    data.isLateFeeEnabled = true;

    if (
      data.status == SubscriptionStatus.Active &&
      location?.state?.isExpired === true
    ) {
      toast.error(
        "This application has expired and the payment hold can no longer be captured. The parker will need to re-apply."
      );
      return;
    }

    // Check if we need to show the quantity modal
    if (data.quantity > quantity && data.isAccountsReceivable) {
      // If start date is after current date, force applyNow and skip modal
      const currentDate = LocalDate.now();
      const startDate = LocalDate.parse(data.start);
      
      if (startDate.isAfter(currentDate)) {
        const dataWithApplyNow = { ...data, applyQuantityChangeNow: true };
        await saveSubscription(dataWithApplyNow);
        return;
      }
      
      setPendingSubmit(data);
      setModalSelection(undefined);
      setShowQuantityModal(true);
      return;
    }

    // Check if we need to show the decrease modal checking the spots allocated
    if (data.quantity < numberOfSpotsAllocated) {
      setPendingSubmit(data);
      setShowQuantityDecreaseParkersModal(true);
      return;
    }

    // Check if we need to show the decrease modal
    if (data.quantity < quantity && data.isAccountsReceivable) {
      setPendingSubmit(data);
      setShowQuantityDecreaseModal(true);
      return;
    }

    // If no modal needed, proceed with save
    await saveSubscription(data);
  };

  const onSubmit = async (data: SubscriptionUpdateReqApi) => {
    await onSubmitInternal(data, false);
  };

  const onSubmitFinalize = async (data: SubscriptionUpdateReqApi) => {
    await onSubmitInternal(data, true);
  };

  const handleFinalizeClick = () => {
    handleSubmit((data) => {
      setPendingFinalizeData(data);
      setShowFinalizeConfirm(true);
    })();
  };

  const filteredOptions: StatusOption[] = [
    { lbl: "Do Nothing", val: sub.status, expl: "Status will not be changed" },
    ...filterStatusOptions(sub.status),
  ];

  const filterStatusOptionsForToggle = (
    status: SubscriptionStatus
  ): StatusOption[] => {
    if (status === SubscriptionStatus.Cancelled) {
      return [cancel];
    }
    if (status === SubscriptionStatus.Active) {
      return [reactivate];
    }
    return [];
  };
  const toggleOptions = filterStatusOptionsForToggle(sub.status);

  const changeSubscriptionStatus = async (
    selectedStatus: SubscriptionStatus
  ) => {
    const res = await Api.changeSubscriptionStatus(
      subscriptionId,
      selectedStatus
    );
    processResult(
      res,
      (updatedSub) => {
        setSub((prev) => ({
          ...prev,
          status: selectedStatus,
        }));
        setShowCancellationModal(false);
        setShowReactivationModal(false);
      },
      (err) => {
        console.error("Failed to change subscription status:", err);
      }
    );
  };

  const furthestAccessPeriodDate: LocalDate | null = sub.accessPeriods.reduce(
    (latest, ap) => (ap.end.isAfter(latest.end) ? ap : latest),
    sub.accessPeriods[0]
  )?.end;

  const handlePlanReactivate = async () => {
    if (furthestAccessPeriodDate?.isAfter(LocalDate.now())) {
      await changeSubscriptionStatus(SubscriptionStatus.Active);
    } else {
      setShowReactivationModal(true);
    }
  };

  const checkPlanDidNotRenew = () => {
    return furthestAccessPeriodDate?.isBefore(LocalDate.now());
  }

  const handleActivateNextCycle = async () => {
    await changeSubscriptionStatus(SubscriptionStatus.Active);
    setShowReactivationModal(false);
  };

  // Fetch renewal failure detail when plan did not renew
  const [renewalFailureDetail, setRenewalFailureDetail] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    if (checkPlanDidNotRenew()) {
      subscriptionStaffApi
        .getRenewalFailureDetail({ subscriptionId: sub.subscriptionId })
        .then((resp) => {
          if (!isMounted) return;
          setRenewalFailureDetail(resp.data?.failedMsg ?? null);
        })
        .catch(() => {
          if (!isMounted) return;
          setRenewalFailureDetail(null);
        });
    } else {
      setRenewalFailureDetail(null);
    }
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sub.subscriptionId]);

  const handleDecreaseModalConfirm = () => {
    if (pendingSubmit) {
      setShowQuantityDecreaseModal(false);
      setPendingSubmit(null);
      saveSubscription(pendingSubmit);
    }
  };

  const handleModalConfirm = () => {
    if (modalSelection !== undefined && pendingSubmit) {
      const data = { ...pendingSubmit, applyQuantityChangeNow: modalSelection };
      setShowQuantityModal(false);
      setPendingSubmit(null);
      saveSubscription(data);
    }
  };

  function parkerInvite(data: InviteParkerForm) {

    Api.subParkerInvite(facilityId, sub.subscriptionId, {
      emailAddress: data.emailAddress,
      name: data.name,
      quantity: 1,
      subscriptionId: sub.subscriptionId,
      isLoginEnabled: data.isLoginEnabled,
      subscriptionGroupId: null,
      startDate: localDateToInstantUtc(data.startDate)
    }).then((resp) =>
      processResult(
        resp,
        (val) => {
          setParkers([...parkers, parseSubscriptionParker(val)]);
        },
        (str) => {
          toast.error(str);
        }
      )
    );
  }

  function parkerRemove(parker: ParkerInfo): void {

    Api.subParkerRemove(facilityId, sub.subscriptionId, {
      parkerId: parker.parkerId,
    }).then((resp) =>
      processResult(
        resp,
        (val) => {
          const updated = parkers.filter(
            (p) => p.parker.parkerId !== parker.parkerId
          );
          setParkers([...updated]);
        },
        (err) => {
          alert(err);
        }
      )
    );
  }

  const saveSubscription = async (data: SubscriptionUpdateReqApi) => {
    setLoading(true);
    
    Api.facilitySubscriptionEditUpdate(
      facilityId,
      subscriptionId,
      parseSubscriptionUpdateReq(data)
    ).then((res) =>
      processResult(
        res,
        (val) => {
          setLoading(false);   
          reset(data);
          if (data.status === SubscriptionStatus.FinalizeSetup) {
            toast.success("Subscription Invitation Sent Successfully");
          }
          else{
            toast.success("Subscription Updated Successfully");
          }
         
          // If we came from the invite page, navigate directly to parker list
          const fromInvite = location.state?.fromInvite || 
                            (location.state?.from === "invite") ||
                            (typeof location.state?.previousPath === 'string' && location.state.previousPath.includes('/parker/invite'));
          if (fromInvite) {
            nav(`/facility/${facilityId}/parker`);
          } else {
            nav(-1);
          }       
        },
        (err) => {
          setMsg(err);
          toast.error(
            "This application has expired and the payment hold can no longer be captured. The parker will need to re-apply."
          );
          setLoading(false);
          return;
        }
      )
    );
  };

  const openEditGroup = (group: ServerRequestsSubscriptionGroupsResponse) => {
    setEditingGroup(group);
    setShowGroupModal(true);
  };

  async function createGroup(
    name: string
    ) {
    const res = await subGroupsApi.addGroup({
        serverRequestsSubscriptionGroupAddRequest: {
        subscriptionId: sub.subscriptionId,
        name,
        },
    });

    const group = res.data;

    if (group?.error) {
        toast.error(group.error);
        return;
    }

    setGroups(prev => [
        ...prev,
        {
        id: group.id,
        name: group.name,
        parkerCount: 0,
        },
    ]);

    toast.success('Group created successfully.');

    return group;
    }

    async function updateGroup(
    groupId: number,
    name: string
    ) {
    const res = await subGroupsApi.updateGroup({
        subscriptionGroupId: groupId,
        name,
        },
    );

    const group = res.data;

    if (group?.error) {
        toast.error(group.error);
        return;
    }

    setGroups(g =>
        g.map(gr =>
            gr.id === groupId ? { ...gr, name } : gr
        )
    );

    toast.success('Group updated successfully.');

    return group;
    }

    async function deleteGroup(groupId: number) {
    const res = await subGroupsApi.deleteGroup({
        subscriptionGroupId: groupId,
        },
    );

    if (res.data?.error) {
        toast.error(res.data?.error);
        return;
    }

    setGroups(g =>
        g.filter(gr => gr.id !== groupId)
    );

    toast.success('Group removed successfully.');
    }

  const handleContractClick = (g: ServerRequestsSubscriptionGroupsResponse) => {
    const rulesToEdit = g.contractRules?.map((r) => ({
      min: r.min!,
      max: r.max!,
      price: r.price!,
      noCost: r.price == null || r.price === 0,
      type: r.price! > 0 ? RuleType.Fee : RuleType.Discount,
    }));
    setEditingRules(rulesToEdit!);
    setEditingGroupId(g.id);
    setShowAddContract(true);
  };

  const modalGroups = editingRules?.length
    ? groups.map(g => ({ id: g.id!, name: g.name! }))
    : groups
        .filter(g => !g.contractRules || g.contractRules.length === 0)
        .map(g => ({ id: g.id!, name: g.name! }));

  const resetContractModal = () => {
    setEditingRules([]);
    setEditingGroupId(undefined);
    setShowAddContract(false);
  };

  const parkersColumns = useMemo<ColDef[]>(
    () => [
      {
        field: "name",
        headerName: "Name",
        sortable: true,
        filter: true,
        valueGetter: (params: any) => {
          // This ensures clean text export
          return params.data.parker.name;
        },
        cellRenderer: (params: any) => {
          const { data } = params;
          const isActive = data.subStatus === SubscriptionStatus.Active;

          return (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
              }}
            >
              {isActive ? (
                <Hyperlink
                  onClick={() => {
                    setSelectedParker(params.data);
                    setSelectedParkerSub({subscriptionId: loadedSub.subscriptionId, status: loadedSub.status, 
                      facilityHasIntegrationsEnabled: loadedSub.facility.hasIntegrationsEnabled, parkerId: loadedSub.parkerId});
                    setShowParkerEditModal(true);
                  }}
                >
                  {data.parker.name}
                </Hyperlink>
              ) : (
                <span>{data.parker.name}</span>
              )}
            </div>
          );
        },
      },
      {
        field: "email",
        headerName: "Email",
        sortable: true,
        filter: true,
        valueGetter: (params: any) => {
          // This ensures clean text export
          return params.data.parker.isManaged ? params.data.parker.emailAddress : "N/A";
        },
        cellRenderer: (params: any) => {
          const { data } = params;
          const isActive = data.subStatus === SubscriptionStatus.Active;
          const email = data.parker.isManaged ? data.parker.emailAddress : "N/A";

          return (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
              }}
            >
              <span>{email}</span>
              {isActive && (
          <button
            className="btn btn-sm"
            title="Delete Parker"
            onClick={() => {
              setRemoveParker(data.parker);
              setShowRemove(true);
              }}
              >
               <Trash />
           </button>
              )}
            </div>
          );
        },
      },
    ],
    []
  );

  const Parkers = () => {
    return (
      <Container fluid>
        <Row>
          <Col style={gridOneCol("max-content 1fr 5px")}>
            <Row className="align-items-center justify-content-between">
              <Col>
                <PageBar title="Parkers" />
              </Col>
              {sub.status == SubscriptionStatus.Active && (
                <Col xs="auto">
                  <Link to="" onClick={() => setShowInviteParker(true)}>
                    Add Parker
                  </Link>
                </Col>
              )}
            </Row>

            <div className="w-100 mb-4">
              <DataGrid
                rowData={[...parkers]
                  .sort((a, b) => a.parker.name.localeCompare(b.parker.name))
                  .map((p) => ({ ...p, subStatus: sub.status }))}
                columnDefs={parkersColumns}
                domLayout="autoHeight"
              />
            </div>
          </Col>
        </Row>
      </Container>
    );
  };

  const groupsColumns = useMemo<ColDef[]>(
    () => [
      {
        field: "name",
        headerName: "Group",
        sortable: true,
        filter: true,
        valueGetter: (params: any) => params.data.name,
        cellRenderer: (params: any) => {
          const g = params.data;

          return (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                gap: 8,
              }}
            >
              <div
                className="me-auto text-truncate fw-semibold"
                style={{ maxWidth: "70%" }}
              >
                <Hyperlink onClick={() => openEditGroup(g)}>
                  {g.name}
                </Hyperlink>

                <span className="badge bg-secondary ms-2">
                  {g.parkerCount} parkers
                </span>

                {g.contractRules?.length > 0 && (
                  <span className="badge bg-success ms-2">
                    Contract
                  </span>
                )}
              </div>

              <Button
                variant="link"
                className="p-0 border-0 shadow-none"
                title={g.contractRules?.length > 0 ? "Edit Contract" : "Add Contract"}
                onClick={() => handleContractClick(g)}
              >
                <FileText className="icon-shift-up" />
              </Button>

              <Button
                variant="link"
                className="p-0 border-0 shadow-none"
                title="Manage parkers"
                onClick={() =>
                  nav(
                    `/facility/${facilityId}/parker/${parker.parkerId}/plan/${sub.subscriptionId}/groups/parkers?groupId=${encodeURIComponent(
                      g.id
                    )}`
                  )
                }
              >
                <PeopleFill className="icon-shift-up" />
              </Button>

              {(sub.status == SubscriptionStatus.Active || sub.status === SubscriptionStatus.NeedsSetup) && (
                <CloseButton
                  onClick={() => {
                    setGroupToRemove(g);
                    setShowRemoveGroup(true);
                  }}
                />
              )}
            </div>
          );
        },
      },
    ],
    []
  );

  const Groups = () => {
    return (
      <Container fluid>
        <Row>
          <Col>
            <Row className="align-items-center justify-content-between">
              <Col>
                <PageBar title="Groups" />
              </Col>

              <Col xs="auto">
                {canManageGroups && (
                  <div className="d-flex align-items-center gap-2">
                    {groups.length > 0 && (
                      <>
                        {modalGroups.length > 0 && (
                          <>
                            <Link
                              to=""
                              onClick={(e) => {
                                e.preventDefault();
                                setShowAddContract(true);
                              }}
                            >
                              Add Contract
                            </Link>

                            <span className="text-muted mx-1">|</span>
                          </>
                        )}

                        <Link
                          to={`/facility/${facilityId}/parker/${parker.parkerId}/plan/${sub.subscriptionId}/groups/parkers`}
                        >
                          Manage Plan
                        </Link>

                        <span className="text-muted mx-1">|</span>
                      </>
                    )}

                    <Link
                      to=""
                      onClick={(e) => {
                        e.preventDefault();
                        setEditingGroup(undefined);
                        setShowGroupModal(true);
                      }}
                    >
                      Add Group
                    </Link>
                  </div>
                )}
              </Col>
          
            </Row>

            <div className="w-100 mb-4">
              {groups.length > 0 ? (
                <DataGrid
                  rowData={[...groups].sort((a, b) =>
                    a.name!.localeCompare(b.name!)
                  )}
                  columnDefs={groupsColumns}
                  domLayout="autoHeight"
                />
              ) : (
                <div className="d-flex flex-column align-items-center justify-content-center text-center py-5 border rounded-bottom">
                  <div
                    className="rounded-circle mb-3"
                    style={{
                      width: 72,
                      height: 72,
                      backgroundColor: "rgba(220, 53, 69, 0.15)",
                    }}
                  />

                  <p className="text-secondary mb-4">
                    Create your first group to organize tiers of parkers
                  </p>

                  {canManageGroups && (
                    <Button
                      className="btn-main px-4"
                      onClick={() => {
                        setEditingGroup(undefined);
                        setShowGroupModal(true);
                      }}
                    >
                      Create Group
                    </Button>
                  )}
                </div>
              )}
            </div>
          </Col>
        </Row>
      </Container>
    );
  };

  const accessPeriodColumns = useMemo<ColDef[]>(
    () => [
      {
        field: "quantity",
        headerName: "Spots",
        sortable: true,
        filter: true,
        valueGetter: (p) => p.data.quantity,
      },
      {
        field: "start",
        headerName: "Start",
        sortable: true,
        filter: true,
        valueFormatter: (p) => showDate(p.data.start),
      },
      {
        field: "end",
        headerName: "End",
        sortable: true,
        filter: true,
        valueFormatter: (p) => showDate(p.data.end),
      },
    ],
    []
  );

  const AccessPeriod = () => {
    return (
      <Container fluid>
        <Row>
          <Col style={gridOneCol("max-content 1fr 5px")}>
            <PageBar title="Access Periods" />
            <div style={{ width: "100%" }}>
              <DataGrid
                rowData={[...sub.accessPeriods].sort(
                  (a, b) =>
                    new Date(showDate(b.end)).getTime() -
                    new Date(showDate(a.end)).getTime()
                )}
                columnDefs={accessPeriodColumns}
                domLayout="autoHeight"
              />
            </div>
          </Col>
        </Row>
      </Container>
    );
  };

  function getDisabled(status: SubscriptionStatus) {
    return status !== SubscriptionStatus.Active;
  }

  const actionRequired =
    sub.status == SubscriptionStatus.Applied ||
    sub.status == SubscriptionStatus.NeedsSetup ||
    sub.status == SubscriptionStatus.WaitingForContract;

    const totalSpaces = sub.quantity;
    const totalParkers = parkers.length;

    const unallocatedSpaces = totalSpaces - totalParkers;

    const unassignedCount = useMemo(
        () => parkers.filter((p) => !p.subscriptionGroupId).length,
        [parkers]
    );

    const hasUnassigned = unassignedCount > 0;
    const hasUnallocated = unallocatedSpaces > 0;

    const showWarning = hasUnassigned || hasUnallocated;

  async function saveContract(data: {
    groupId: number;
    rules: ContractRuleForm[];
  }) {
    // REMOVE UI-ONLY FIELDS
    const cleanRules = (data.rules as any[]).map(
      ({ _priceText, _isPriceFocused, id, ...rule }) => rule
    );

    const res =
      await contractsApi.contractsRulePost({
        serverRequestsContractsRuleSaveRequest: {
          subscriptionGroupId: data.groupId,
          rules: cleanRules,
        },
      });

    const result = res.data;

    if (result?.error) {
      toast.error(result.error);
      return;
    }
  
    setGroups((prev) =>
      prev.map((g) =>
        g.id === data.groupId
          ? {
              ...g,
              contractRules: result.rules ?? [],
            }
          : g
      )
    );

    resetContractModal();

    toast.success("Contract created successfully.");
    return result;
  }

  async function removeContract(groupId: number) {
    try {
      const res = await contractsApi.contractsRuleSubscriptionGroupIdDelete({
        subscriptionGroupId: groupId,
      });

      const result = res.data;

      if (result?.error) {
        toast.error(result.error);
        return;
      }

      setGroups((prev) =>
        prev.map((g) =>
          g.id === groupId
            ? {
                ...g,
                contractRules: [], 
                hasContract: false,
              }
            : g
        )
      );

      resetContractModal();

      toast.success("Contract removed successfully.");
    } catch (err) {
      toast.error("Failed to remove contract.");
    }
  }

    const refreshCostProjection = async () => {
    const subscriptionApi = createApiService(SubscriptionApi);
    const response = (await subscriptionApi.subscriptionIdCostProjectionGet({subscriptionId: sub.subscriptionId})).data!;

    if (response.currentPeriod) {
        const mappedCurrent = mapCostProjection(
        response.currentPeriod,
        parkers,
        );
        setCurrentProjection(mappedCurrent);
    }

    if (response.nextPeriod) {
        const mappedNext = mapCostProjection(
        response.nextPeriod,
        parkers, 
        );
        setNextProjection(mappedNext);
    }
    };

  return (
    <MediumPage>
      {(() => {
        const subscription = parker.subscriptions.find(
          (s) => s.subscriptionId === sub.subscriptionId
        );

        return (
          <>
            <div className="mb-3">
              <h3 className="fw-bold mb-1">
                <BsButton
                  variant="link"
                  style={{ margin: "-10px 0 0 -10px" }}
                  onClick={() => nav(-1)}
                >
                  ← Back
                </BsButton>
                {sub.facility.name}
              </h3>

              <div className="d-flex align-items-center gap-2 mb-1">
                {checkPlanDidNotRenew() ? (
                  <span className="badge bg-primary d-inline-flex align-items-center opacity-75">
                    <ExclamationCircleFill className="me-1" />
                    Renew Failed
                  </span>
                ) : (
                  <span className="badge bg-primary">
                    {showSubscriptionStatus(sub.status)}
                  </span>
                )}

                <span className="fw-semibold text-muted">
                  {subscription?.plan.name}
                </span>
              </div>

              <div className="d-flex justify-content-between align-items-center">
                <div className="text-muted small">
                  {getInstructions(
                    sub.status,
                    furthestAccessPeriodDate,
                    (sub.paymentMethod !== null && sub.paymentMethod.length > 0),
                    sub.end
                  )}
                </div>
              </div>
            </div>

            <div className="border-top mt-3 mb-3"></div>

            {actionRequired && (
              <div className="alert alert-warning d-flex flex-row align-items-center gap-1 p-2 mb-0">
                <ExclamationCircleFill color="danger" />{" "}
                {getActionsInstructions(sub.status)}
              </div>
            )}

            {renewalFailureDetail && (
              <div className="alert alert-warning d-flex flex-row align-items-center gap-1 p-2 mt-1">
               
                <span>{renewalFailureDetail}</span>
              </div>
            )}

            <div className="d-flex justify-content-between align-items-center mb-3">

              <div className="d-flex gap-3 flex-grow-1">
                {(sub.status == SubscriptionStatus.Active ||
                  sub.status == SubscriptionStatus.Cancelled) &&
                  toggleOptions.map((f, index) => (
                    <div className="form-check form-switch" key={f.val}>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        id={`switch-${index}`}
                        checked={sub.status === SubscriptionStatus.Active}
                        onChange={() => {
                          if (f.val === SubscriptionStatus.Active) {
                            handlePlanReactivate();
                          } else {
                            setShowCancellationModal(true);
                          }
                        }}
                      />
                      <label className="form-check-label" htmlFor={`switch-${index}`}>
                        {f.lbl}
                      </label>
                      <div>{f.expl}</div>
                    </div>
                  ))}
              </div>

            {isAdminRole(auth.account?.roles ?? []) && !actionRequired && (
              <Button
                size="sm"
                variant="outline-secondary"
                className="p-1 px-2"
                onClick={openAuditLog}
              >
                <JournalText className="me-1 mb-1" /> Audit Log
              </Button>
            )}
            </div>

            <div className="row g-3 border rounded p-4 mb-4 mt-3">
              {(sub.status === SubscriptionStatus.Applied) && (
                <div className="d-flex flex-wrap gap-3 align-items-start justify-content-start">
                  {filteredOptions.map((f) => {
                    const selected = f.val === getValues("status");
                    const isOriginalValue = f.val === sub.status;

                    return (
                      <div
                        key={f.val}
                        className="d-flex flex-column align-items-center text-center"
                        style={{ minWidth: "140px", maxWidth: "200px" }}   // optional for equal width
                      >
                        <BsButton
                          className={
                            selected
                              ? "w-100 bg-primary text-white"
                              : "w-100 bg-white text-secondary border border-primary"
                          }
                          onClick={() => {
                            setValue("status", f.val);
                            setIsStatusDirty(!isOriginalValue);
                          }}
                        >
                          {f.lbl}
                        </BsButton>
                        <small className="mt-2 text-muted" style={{ minHeight: "2em" }}>
                          {f.expl}
                        </small>
                      </div>
                    );
                  })}
                </div>
              )}

                {showWarning && isEnhancedGroup && (
                    <div className="alert alert-warning d-flex align-items-start gap-3 py-3 mb-3">
                        <div className="fs-5 lh-1">⚠️</div>
                        
                          <div>                                
                              <div className="fw-semibold mb-1">Attention Required</div>
                        
                              <div className="small mb-2">
                              The following will be charged the base rate of{" "}
                              <strong>{showMoney(sub.price)}</strong>:
                              </div>
                        
                              <ul className="mb-0 ps-3 small">
                              {hasUnassigned && (
                                  <li>
                                  <strong>{unassignedCount}</strong> parker
                                  {unassignedCount > 1 ? "s are" : " is"} not assigned to a
                                  group
                                  </li>
                              )}
                        
                              {hasUnallocated && (
                                  <li>
                                  <strong>{unallocatedSpaces}</strong> unallocated space
                                  {unallocatedSpaces > 1 ? "s" : ""}
                                  </li>
                              )}
                              </ul>
                              {canManageGroups && (
                                  <div className="mt-2">  
                                    {groups.length > 0 ? (
                                      <Link
                                        to={`/facility/${facilityId}/parker/${parker.parkerId}/plan/${sub.subscriptionId}/groups/parkers`}
                                      >
                                        Manage Plan
                                      </Link>
                                    ) : (
                                      <>
                                        <Link
                                          to=""
                                          onClick={(e) => {
                                            e.preventDefault();
                                            setEditingGroup(undefined);
                                            setShowGroupModal(true);
                                          }}
                                        >
                                          Add Group
                                        </Link>
                                        {" "}to Start Managing the Plan
                                      </>
                                    )}
                                  </div>
                              )}
                          </div>
                  </div>
              )}

             {isEnhancedGroup && (
                  <CostProjectionSection
                      current={currentProjection}
                      next={nextProjection}
                      onLoad={refreshCostProjection}
                  />
              )}

              <div className="col-md-6">
                <Select
                  icon={<CurrencyDollar />}
                  label="Plan Price"
                  explanation={isEnhancedGroup ? "The base price for the plan" : "This will change the parker's next bill"}
                  selectClassName="text-end"
                  disabled={sub.status !== SubscriptionStatus.Active}
                  {...register("planPriceId", {
                    required: "Plan price is required",
                    valueAsNumber: true,
                    onChange: (e) => {
                      const value = parseInt(e.target.value);
                      if (value !== sub.planPriceId) {
                        setValue("planPriceId", value, { shouldDirty: true });
                      }
                    },
                  })}
                  error={errors.planPriceId}
                >
                  {sub.prices?.map((price) => (
                    <option key={price.planPriceId} value={price.planPriceId}>
                      {showMoney(cents(price.price))}
                    </option>
                  ))}
                </Select>
              </div>

              {!isEnhancedGroup && (
                <div className="col-md-6">
                  <Input
                    icon={<Hash />}
                    type="number"
                    label="Quantity"
                    explanation="This will change the parker's next bill"
                    disabled={getDisabled(sub.status)}
                    inputClassName="text-end"
                    min={1}
                    {...register("quantity", {
                      required: "Quantity is required",
                      min: {
                        value: 1,
                        message: "Quantity must be at least 1",
                      },
                      valueAsNumber: true,
                    })}
                    error={errors.quantity}
                  />
                </div>
              )}

              <div className="col-md-6">
                <Input
                  type="date"
                  disabled={sub.status === SubscriptionStatus.Active}
                  label="Start Date"
                  {...register("start", { required: "Start date is required" })}
                  error={errors.start}
                />
              </div>
              
              {sub.end &&
              <div className="col-md-6">
                <Input
                  type="date"
                  disabled
                  label="End Date"
                  {...register("end", { required: "End date is required" })}
                  error={errors.start}
                />
              </div>
              }

              {!isEnhancedGroup && (
                <div className="col-md-6">
                  <Select
                    label="Payment"
                    explanation="Whether payment is required immediately or can be deferred"
                    disabled
                    {...register("isAccountsReceivable", {
                      setValueAs: parseBoolean,
                    })}
                    error={errors.isAccountsReceivable}
                  >
                    <option value="false">Require Payment Immediately</option>
                    <option value="true">Defer to Accounts Receivable</option>
                  </Select>
                </div>
              )}

              <div className="d-flex justify-content-end gap-2 mt-3 mb-3">
                {isEnhancedGroup && sub.status === SubscriptionStatus.NeedsSetup ? (
                  <>
                    <Button
                      className="w-50  btn-main"
                      type="button"
                      loading={loading}
                      disabled={!isStatusDirty && !isDirty}
                      onClick={handleSubmit(onSubmit)}
                    >
                      Save
                    </Button>                   
                  </>
                ) : (
                  <Button
                    className="w-50 btn-main"
                    type="submit"
                    loading={loading}
                    disabled={
                      !isStatusDirty && !isDirty
                    }
                    onClick={handleSubmit(onSubmit)}
                  >
                    Save
                  </Button>
                )}
              </div>
            </div>

            {sub.status !== SubscriptionStatus.NeedsSetup && (
              <>
                <div className="d-flex gap-3 mb-4">
                  <div className="border rounded p-3 flex-fill">
                    <div className="d-flex align-items-center mb-2">
                      <CarFront className="me-2" />
                      <strong>Parking</strong>
                    </div>
                    <div>
                      Plan includes <strong>{sub.quantity}</strong> purchased spot
                      {sub.quantity !== 1 && "s"}
                    </div>
                  </div>

                  {!sub.end &&
                  <div className="border rounded p-3 flex-fill">
                    <div className="d-flex align-items-center mb-2">
                      <CreditCard className="me-2" />
                      <strong>Billing</strong>
                    </div>
                    {furthestAccessPeriodDate && (
                        <div>
                            {(() => {
                                // Create a new date based on furthestAccessPeriodDate but with dayOfMonthInvoice
                                const invoiceDate = LocalDate.of(
                                    furthestAccessPeriodDate.year(),
                                    furthestAccessPeriodDate.month(),
                                    sub.plan.dayOfMonthInvoice
                                );

                                return sub.paymentMethod ? (
                                    <>
                                        Parker will be billed on{" "}
                                        {showDate(furthestAccessPeriodDate)}
                                    </>
                                ) : (
                                    <>
                                        Parker will be invoiced on{" "}
                                        {showDate(invoiceDate)}
                                    </>
                                );
                            })()}
                        </div>
                    )}
                    {sub.paymentMethod && (
                        <div className="text-muted">
                        {sub.paymentMethod}
                        </div>
                    )}
                  </div>
                  }
                </div>

                {loadedSub.facility.hasIntegrationsEnabled && isAdminRole(auth.account?.roles ?? []) && sub.status == SubscriptionStatus.Active && (
                    <ParkerIntegrations
                      integrations={subscriptionIntegrations}
                      subscriptionId={subscriptionId}
                      facilityId={facilityId}
                      setPendingToggle={setPendingToggle}
                      setShowToggleConfirm={setShowToggleConfirm}
                      setQrCodeData={setQrCodeData}
                      setShowQrModal={setShowQrModal}
                    />
                )}
                {isEnhancedGroup && <Groups />}
                {!isEnhancedGroup && <Parkers />}
                
                {!isEnhancedGroup && (
                <VehiclesSection
                  vehicles={sub.vehicles}
                  subscriptionStatus={sub.status}
                  facilityId={facilityId}
                  subscriptionId={sub.subscriptionId}
                  integrations={vehicleIntegrations}
                  onAddNew={() => setShowAddVehicleModal(true)}
                  onAddExisting={openChooseModalForParkersList}
                  onRemove={(v) => {
                    setVehicleToDelete(v);
                    setShowParkerVehicleRemoveConfirm(true);
                  }}
                  setPendingToggle={setPendingToggle}
                  setShowToggleConfirm={setShowToggleConfirm}
                  showVehiclesLabel={true}
                />
                )}
                <AccessPeriod />
              </>
            )}
            {isEnhancedGroup && sub.status === SubscriptionStatus.NeedsSetup && (
              <>
                <Groups />
                <Button
                  className="w-100 btn-main mb-4"
                  type="button"
                  loading={loading}
                  onClick={handleFinalizeClick}
                >
                  Send Invitation
                </Button>
              </>
            )}
          </>
        );
      })()}

      <Modal
        show={showQuantityModal}
        onHide={() => {
          setShowQuantityModal(false);
          setPendingSubmit(null);
        }}
        backdrop="static"
        centered
      >
        <Modal.Header>
          <Modal.Title>When to Apply Additional Spaces</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            Do you want to apply additional spaces now, or wait until the next
            invoice cycle?
            <Alert
              variant="info"
              className="mt-2"
              style={{
                backgroundColor: "var(--color-light)",
                fontSize: ".9rem",
              }}
            >
              If selecting <b>Apply additional spaces to current invoice</b>, the parker will receive an amended invoice with the additional spaces included.
            
            </Alert>
          </div>
          <div className="d-flex flex-column gap-3">
            <div className="form-check">
              <input
                className="form-check-input"
                type="radio"
                id="applyNow"
                checked={modalSelection === true}
                onChange={() => setModalSelection(true)}
              />
              <label className="form-check-label" htmlFor="applyNow">
                Apply additional spaces to current invoice
              </label>
            </div>
            <div className="form-check">
              <input
                className="form-check-input"
                type="radio"
                id="applyNextCycle"
                checked={modalSelection === false}
                onChange={() => setModalSelection(false)}
              />
              <label className="form-check-label" htmlFor="applyNextCycle">
                Wait until next invoice cycle ({furthestAccessPeriodDate ? showDate(LocalDate.of(
                  furthestAccessPeriodDate.year(),
                  furthestAccessPeriodDate.month(),
                  sub.plan.dayOfMonthInvoice
                )) : 'N/A'})
              </label>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              setShowQuantityModal(false);
              setPendingSubmit(null);
            }}
          >
            Cancel
          </Button>
          <Button
            className="btn-main"
            onClick={handleModalConfirm}
            disabled={modalSelection === undefined}
          >
            Save
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showQuantityDecreaseModal}
        onHide={() => {
          setShowQuantityDecreaseModal(false);
          setPendingSubmit(null);
        }}
        backdrop="static"
        centered
      >
        <Modal.Header>
          <Modal.Title>Confirm Space Reduction</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert
            variant="info"
            className="mt-2"
            style={{ backgroundColor: "var(--color-light)", fontSize: ".9rem" }}
          >
            {(() => {
              const currentDate = LocalDate.now();
              const startDate = LocalDate.parse(values.start);
              const isStartDateInFuture = startDate.isAfter(currentDate);
              
              if (isStartDateInFuture && isPositive(parker.balance)) {
                return (
                  <p>
                    <strong>Important:</strong> Since the next period hasn't started yet, 
                    an amended invoice will be sent to the parker with the reduced spaces.
                  </p>
                );
              } else {
                return (
                  <p>
                    <strong>Important:</strong> The period has already started or the parker's balance is
                    paid. Any credits needed will need to be manually applied to the
                    parker's account.
                  </p>
                );
              }
            })()}
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              setShowQuantityDecreaseModal(false);
              setPendingSubmit(null);
            }}
          >
            Cancel
          </Button>
          <Button className="btn-main" onClick={handleDecreaseModalConfirm}>
            I Understand, Save Changes
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showQuantityDecreaseParkersModal}
        onHide={() => {
          setShowQuantityDecreaseParkersModal(false);
          setPendingSubmit(null);
        }}
        backdrop="static"
        centered
      >
        <Modal.Header>
          <Modal.Title>Cannot Reduce Spots</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert
            variant="light"
            className="mt-2 border"
            style={{
              backgroundColor: "var(--color-light)",
              fontSize: ".9rem",
              borderColor: "#dee2e6",
            }}
          >
            <p>
              <strong>Notice:</strong> The plan currently has{" "}
              <strong>{numberOfSpotsAllocated}</strong> allocated spot
              {numberOfSpotsAllocated !== 1 && "s"}. You cannot reduce the
              number of spots in the plan right now.
            </p>
            <p>
              Please manage the parkers' assigned spots before making changes to
              the quantity.
            </p>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              setShowQuantityDecreaseParkersModal(false);
              setPendingSubmit(null);
            }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {selectedParker && (
        <ParkerEditModal
          show={showParkerEditModal}
          onClose={() => setShowParkerEditModal(false)}
          parker={selectedParker!}
          setParker={setSelectedParker}
          parkers={parkers}
          setParkers={setParkers}
          selectedParkerSub={selectedParkerSub!}
          facilityId={facilityId}
        />
      )}

      <ConfirmModal
        title="Remove Parker"
        message="Are you sure you want to remove this parker from the plan?"
        show={showRemove}
        setShow={setShowRemove}
        onConfirm={() => {
          if (removeParker !== undefined) {
            parkerRemove(removeParker);
          }
        }}
      />

      <ConfirmModal
        title={`${pendingToggle?.newStatus === IntegrationStatus.Activated ? "Activate" : "Deactivate"} Integration`}
        message={`Are you sure you want to ${pendingToggle?.newStatus === IntegrationStatus.Activated ? "activate" : "deactivate"} this integration?`}
        show={showToggleConfirm}
        setShow={setShowToggleConfirm}
        onConfirm={() => {
          if (pendingToggle) {
            handleToggleIntegration(
              pendingToggle.integrationPartyId,
              pendingToggle.subscriptionId,
              pendingToggle.parkerId,
              pendingToggle.vehicleId,
              pendingToggle.newStatus
            );
          }
          setShowToggleConfirm(false);
          setPendingToggle(null);
        }}
      />

      <ConfirmModal
        title="Send Invitation?"
        message={
          <>
            An invoice will be sent to the group account when you confirm.
            <br /><br />
            <strong>You or the group account holder can make changes at any time.</strong> An updated invoice will be generated to reflect any changes made.
          </>
        }
        show={showFinalizeConfirm}
        cancelText="Go Back"
        setShow={setShowFinalizeConfirm}
        onConfirm={() => {
          if (pendingFinalizeData) {
            onSubmitFinalize(pendingFinalizeData);
            setPendingFinalizeData(null);
          }
          setShowFinalizeConfirm(false);
        }}
      />

      <InviteParkerModal
        show={showInvite}
        setShow={setShowInviteParker}
        submit={parkerInvite}
        isEnhancedGroup={isEnhancedGroup}
        subscriptionStart={sub.start}
      />

      <ReactivatePlanModal
        show={showReactivationModal}
        onHide={() => setShowReactivationModal(false)}
        onActivateNextCycle={handleActivateNextCycle}
      />
      <UserPlanCancellationModal
        show={showCancellationModal}
        onHide={() => setShowCancellationModal(false)}
        onConfirmCancel={async () =>
          await changeSubscriptionStatus(SubscriptionStatus.Cancelled)
        }
      />
      <QrCodeModal
        show={showQrModal}
        onHide={() => setShowQrModal(false)}
        value={qrCodeData}
        height={256}
      />

      <SubscriptionAuditLogModal
        show={showAudit}
        onHide={() => setShowAudit(false)}
        auditEntries={normalizedAudit}
      />

      <AddVehicleModal
        show={showAddVehicleModal}
        setShow={setShowAddVehicleModal}
        onSubmit={vehicleAdd}
      />

      <ChooseVehicleModal
        show={showChoose}
        setShow={setShowChoose}
        onSubmit={vehicleLink}
        vehicles={chooseModalVehicles}
        exclude={chooseModalExclude}
        onClose={() => setShowChoose(false)}
      />

      <ConfirmModal
        title={`Remove Vehicle`}
        message={`Are you sure you want to remove this vehicle from the parker?`}
        show={showParkerVehicleRemoveConfirm}
        setShow={setShowParkerVehicleRemoveConfirm}
        onConfirm={() => {
          if (vehicleToDelete)
          {
            vehicleRemove(vehicleToDelete)
          }
          setVehicleToDelete(null)
          setShowParkerVehicleRemoveConfirm(false);
        }}
      />

      <GroupModal
            show={showGroupModal}
            onClose={() => setShowGroupModal(false)}
            group={editingGroup}
            existingGroups={groups}
            onSubmit={async (name, groupId, options) => {
              if (groupId) {
                await updateGroup(groupId, name);
              } else {
                const createdGroup = await createGroup(name);
                if (
                  createdGroup &&
                  options?.addContractAfterSave &&
                  createdGroup.id != null
                ) {
                  setEditingGroupId(createdGroup.id);
                  setShowAddContract(true);
                }
              }
            }}
      />

      <ConfirmModal
            title="Remove Group"
            message={
            groupToRemove ? (
                <>
                  Are you sure you want to remove this group?{" "}
                  {groupToRemove.parkerCount! > 0 && (
                    <strong>
                      All {groupToRemove.parkerCount} parkers
                      {groupToRemove.contractRules && groupToRemove.contractRules!.length > 0 && " and the contract "}
                      will be removed from the group.
                    </strong>
                  )}

                  {groupToRemove.parkerCount! === 0 && groupToRemove.contractRules && groupToRemove.contractRules!.length > 0 && (
                    <strong>
                      The contract will be removed from the group.
                    </strong>
                  )}
                </>
            
            ) : null
            }
            show={showRemoveGroup}
            setShow={setShowRemoveGroup}
            onConfirm={() => {
                if (groupToRemove?.id) {
                deleteGroup(groupToRemove.id);
                }
            }}
      />

      {showAddContract && (
      <ContractRuleModal
        show={showAddContract}
        onClose={() => { setShowAddContract(false); resetContractModal();}}
        context={editingGroupId ? { mode: "group", groupId : editingGroupId!} : { mode: "plan" }}
        groups={modalGroups}
        onSubmit={async (data) => {
          await saveContract(data); 
        }}
        isEditing={editingRules?.length > 0}
        initialRules={editingRules}
        onRemoveContract={removeContract}
      />
    )}

    </MediumPage>
  );
}